import {
  createContext,
  useContext,
  useEffect,
  useState,
  type FC,
  type ReactNode
} from "react";

import type { HeaderCrumb } from "./HeaderBreadcrumb";

export type AppHeaderConfig = {
  /**
   * Breadcrumb for the center pill; falls back to the route default. The
   * nearest linked parent crumb is also where Back goes when the session has
   * no in-app history.
   */
  crumbs?: HeaderCrumb[];
  /** Extra controls after the sidebar trigger (e.g. a page's own panel toggle). */
  left?: ReactNode;
  /** Right-aligned content (chips, toggles, actions). */
  right?: ReactNode;
};

type Setter = (config: AppHeaderConfig | null) => void;

// Two contexts so pages that *register* content never re-render when the
// header *reads* it (which would loop with inline config objects).
const ConfigContext = createContext<AppHeaderConfig | null>(null);
const SetterContext = createContext<Setter | null>(null);

export const AppHeaderProvider: FC<{ children: ReactNode }> = ({
  children
}) => {
  const [config, setConfig] = useState<AppHeaderConfig | null>(null);
  return (
    <SetterContext.Provider value={setConfig}>
      <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
    </SetterContext.Provider>
  );
};

/** Read the currently registered header config (used by `AppHeader`). */
export const useAppHeaderConfig = (): AppHeaderConfig | null =>
  useContext(ConfigContext);

/**
 * Register a page's header content for as long as the page is mounted.
 * Safe to call outside the provider (no-op), e.g. in isolated tests.
 *
 * The config is re-applied whenever its identity changes; pass a memoized
 * object when the page re-renders often. Pass `null` to register nothing
 * (e.g. when the component is embedded inside another page).
 */
export function useAppHeader(config: AppHeaderConfig | null): void {
  const setConfig = useContext(SetterContext);

  useEffect(() => {
    if (!setConfig || !config) return undefined;
    setConfig(config);
    return () => setConfig(null);
  }, [config, setConfig]);
}
